import { FC, useState, useMemo, useEffect } from 'react';
import { useStore } from 'hooks/useRootStore';
import { useQuery, useMutation } from 'react-query';
import { useAppPreloader } from 'hooks/useAppPreloader';
import { observer } from 'mobx-react-lite';
import { GridColumns, DataGrid, GridCellParams } from '@material-ui/data-grid';
import IconButton from '@material-ui/core/IconButton';
import PersonIcon from '@material-ui/icons/Person';
import SyncIcon from '@mui/icons-material/Sync';
import SyncDisabledIcon from '@material-ui/icons/SyncDisabled';
import { useStyles } from './styles';
import { Paper, Tooltip, Snackbar, SnackbarContent } from '@material-ui/core';
import EmailIcon from '../../../assets/icons/EndUser/Email.svg';
import { toFormatDateTime } from 'utils';
import { green, red } from '@material-ui/core/colors';
import { getRowsPerPageOptions } from 'utils/pagination';

export const ZoomSyncUser: FC = observer(() => {
  const classes = useStyles();
  const [loadingMessage, setLoadingMessage] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const {
    zoomStore: { syncUser, allsyncUsersView, reSyncUser, reSyncSingleUser },
    dashboardStore: { generalStatistic, getStatistic },
    ldapStore: { usersView, getUsers },
    notificationStore: { openNotification },
  } = useStore();
  const syncUserReq = useQuery('syncUser', () => syncUser(), {
    onError: () => openNotification('error', `We are unable to get the user from Zoom`),
  });
  const getUsersReq = useQuery('getUsersReq', () => getUsers(), {
    onError: () => openNotification('error', `Can't get users`),
  });
  const getStatisticReq = useQuery('getStatisticReq', () => getStatistic(), {
    onError: () => openNotification('error', `Can't get statistic`),
  });
  const reSyncUserReq = useMutation(() => reSyncUser(), {
    mutationKey: 'reSyncUser',
    onSuccess: () => {
      setOpen(false);
      const syncedUserIds = new Set(
        allsyncUsersView.map((user) => user.zoomId).filter((id): id is string => id !== null)
      );
      setSyncedUsers(syncedUserIds);
      openNotification('success', 'Bi-direction Sync Successful');
      syncUserReq.refetch();
      getUsersReq.refetch();
    },
    onError: ({ message }: Error) => {
      setOpen(false);
      openNotification('error', message);
    },
  });
  const reSyncSingleUserReq = useMutation((data: { email: string; zoomId: string }) => reSyncSingleUser(data), {
    mutationKey: 'reSyncSingleUserReq',
    onSuccess: (_, variables) => {
      openNotification('success', 'Selected Users sync’ed with App Zoom Successfully');
      setSyncedUsers((prev) => new Set(prev).add(variables.zoomId));
      syncUserReq.refetch();
      getUsersReq.refetch();
    },
    onError: (error: any) => {
      setOpen(false);
      openNotification('error', error.data.message);
    },
  });

  useEffect(() => {
    document.title = 'EasyIdentity: App: SyncUser';
  }, []);

  const user = usersView.map((item: { zoomId: any }) => item.zoomId);
  const filteredUserSet = new Set(user);
  const [syncedUsers, setSyncedUsers] = useState<Set<string>>(new Set());
  const rows = useMemo(() => {
    return !syncUserReq.isFetching ? allsyncUsersView : [];
  }, [syncUserReq.isFetching, allsyncUsersView]);
  const [pageSize, setPageSize] = useState(10);
  const rowsPerPageOptions = useMemo(() => getRowsPerPageOptions(rows), [rows]);
  const handleSync = (email: string, zoomId: string) => {
    reSyncSingleUserReq.mutate({ email, zoomId });
  };
  const handleSyncAll = () => {
    setLoadingMessage('Sync is in Progress. Please be patient, it may take few minutes.');
    reSyncUserReq.mutate();
    setOpen(true);
  };
  const columns: GridColumns = [
    {
      field: 'icon',
      headerName: ' ',
      width: 40,
      align: 'center',
      disableColumnMenu: true,
      renderCell: (params: GridCellParams) => <PersonIcon style={{ color: 'grey' }} />,
    },
    {
      field: 'creationTimestamp',
      headerName: 'CREATED',
      flex: 0.9,
      renderCell: (params: GridCellParams) => (
        <Tooltip title={toFormatDateTime(params.row.creationTimestamp)} placement='bottom'>
          <div style={{ display: 'flex', gap: '10px' }}>{toFormatDateTime(params.row.creationTimestamp)}</div>
        </Tooltip>
      ),
    },
    {
      field: 'updationTimestamp',
      headerName: 'UPDATED',
      flex: 0.9,
      renderCell: (params: GridCellParams) => (
        <Tooltip title={toFormatDateTime(params.row.updationTimestamp)} placement='bottom'>
          <div style={{ display: 'flex', gap: '10px' }}>{toFormatDateTime(params.row.updationTimestamp)}</div>
        </Tooltip>
      ),
    },
    {
      field: 'name',
      headerName: 'FULL NAME',
      flex: 0.8,
      renderCell: (params: GridCellParams) => (
        <Tooltip title={(params.row.first_name || '') + ' ' + (params.row.last_name || '')}>
          <div>
            <span>
              {params.row.first_name} {params.row.last_name}
            </span>
          </div>
        </Tooltip>
      ),
    },
    {
      field: 'email',
      headerName: 'EMAIL',
      flex: 1,
      renderCell: (params: GridCellParams) => {
        const value = String(params.value ?? '');
        return (
          <Tooltip title={value || ''} placement='bottom'>
            <div style={{ display: 'flex', gap: '10px' }}>
              <img src={EmailIcon} alt='' />
              <span>{value}</span>
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'ACTIONS',
      width: 160,
      disableColumnMenu: true,
      renderCell: (params: GridCellParams) => (
        <>
          {!filteredUserSet.has(params.row.zoomId) && !syncedUsers.has(params.row.zoomId) ? (
            <Tooltip title='Not Sync'>
              <IconButton size='small' onClick={() => handleSync(params.row.email, params.row.zoomId)}>
                <SyncDisabledIcon style={{ color: red[500] }} />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title='Sync'>
              <IconButton size='small' onClick={() => handleSync(params.row.email, params.row.zoomId)}>
                <SyncIcon style={{ color: green[500] }} />
              </IconButton>
            </Tooltip>
          )}
        </>
      ),
    },
  ];

  const ldapUserCount = usersView.length ?? 0;
  const zoomUserCount = allsyncUsersView.length ?? 0;
  // const difference = Math.abs(ldapUserCount - zoomUserCount);

  const disabledSyncCount = rows
    .filter((row) => row.zoomId !== null)
    .filter((row) => row.zoomId && !filteredUserSet.has(row.zoomId) && !syncedUsers.has(row.zoomId)).length;

  useAppPreloader(
    syncUserReq.isFetching ||
      reSyncUserReq.isLoading ||
      getUsersReq.isFetching ||
      reSyncSingleUserReq.isLoading ||
      getStatisticReq.isFetching
  );
  return (
    <>
      {!syncUserReq.isFetching && !getUsersReq.isFetching && !getStatisticReq.isFetching && (
        <Paper className={classes.dataGrid1} elevation={0}>
          <>
            <h1 className={classes.textBox}>You have {ldapUserCount} Users in EasyIdentity.</h1>
            <h1 className={classes.textBox}>You have {zoomUserCount} Users in Zoom Workspace</h1>
            <h1 className={classes.textBox}>
              Sync Users : EasyIdentity {'<->'} Zoom Workspace
              <SyncIcon style={{ color: green[500], cursor: 'pointer' }} onClick={handleSyncAll} />
            </h1>
            {disabledSyncCount > Number(generalStatistic?.leftAccounts) && (
              <h1 className={classes.textBox}>
                There are only {generalStatistic?.leftAccounts || '0'} Easyidentity User Licenses remaining. Please
                upgrade your subscription to Sync Easyidentity User Accounts with Zoom Workspace.
              </h1>
            )}
          </>
        </Paper>
      )}
      <Paper className={classes.dataGrid1} elevation={0}>
        <>
          <h1 style={{ fontWeight: '400', fontSize: '120%', textAlign: 'center' }}>List of All Zoom Workspace Users</h1>
        </>
      </Paper>
      <Paper className={classes.dataGrid} elevation={0}>
        <DataGrid
          className={classes.noBorder}
          autoHeight
          rows={rows}
          columns={columns}
          pageSize={pageSize}
          rowsPerPageOptions={rowsPerPageOptions}
          pagination
          onPageChange={(params) => setPageSize(params.pageSize)}
        />
      </Paper>
      <Snackbar open={open}>
        <SnackbarContent style={{ backgroundColor: green[500] }} message={loadingMessage} />
      </Snackbar>
    </>
  );
});
